<template>
    <div class="system-prompt-drawer-content">
        <div class="system-prompt-header">
            <div class="system-prompt-title">{{ title }}</div>
            <button class="system-prompt-close" :disabled="saving" @click="$emit('close')">
                ×
            </button>
        </div>
        <div class="system-prompt-main-area">
            <div class="system-prompt-subtitle">{{ subtitle }}</div>
            <div class="system-prompt-textarea-wrapper">
                <textarea v-model="model" class="system-prompt-textarea" :placeholder="placeholder" :disabled="saving"
                    :maxlength="maxLength" />
                <span class="system-prompt-char-count" :class="{ 'is-over': charCount > maxLength }">
                    {{ charCount }}/{{ maxLength }}
                </span>
            </div>
        </div>
        <div class="system-prompt-footer">
            <button class="system-prompt-btn system-prompt-btn-cancel" :disabled="saving" @click="$emit('close')">
                {{ t('chat.cancel') }}
            </button>
            <button class="system-prompt-btn system-prompt-btn-save" :disabled="saving || charCount > maxLength"
                @click="$emit('save')">
                {{ saving ? t('chat.saving') : t('chat.save') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '@/composables/useI18n'

const { t } = useI18n()

withDefaults(defineProps<{
    title: string
    subtitle: string
    placeholder: string
    saving?: boolean
    maxLength?: number
}>(), {
    saving: false,
    maxLength: 3000,
})

defineEmits<{
    (e: 'close'): void
    (e: 'save'): void
}>()

const model = defineModel<string>({ default: '' })
const charCount = computed(() => model.value.length)
</script>

<style scoped lang="less">
.system-prompt-drawer-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    border-left: 1px solid rgba(255, 255, 255, 0.10);
    background: var(--, #1A1A1C);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.system-prompt-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid #1F1F21;
}

.system-prompt-title {
    font-size: 16px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
}

.system-prompt-close {
    width: 20px;
    height: 20px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 30px;
    line-height: 20px;

    &:hover {
        background: rgba(38, 38, 42, 0.9);
        border-color: rgba(255, 255, 255, 0.12);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.system-prompt-main-area {
    flex: 1;
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    overflow-y: auto;
}

.system-prompt-subtitle {
    color: rgba(255, 255, 255, 0.50);
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-bottom: 10px;
}

.system-prompt-textarea-wrapper {
    position: relative;
}

.system-prompt-textarea-wrapper {
    position: relative;
}

.system-prompt-textarea {
    display: flex;
    height: 288px;
    min-height: 288px;
    max-height: 288px;
    padding: 14px;
    padding-bottom: 32px;
    padding-bottom: 32px;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: #1C1C20;
    width: 100%;
    resize: none;
    color: rgba(255, 255, 255, 0.40);
    font-family: Roboto;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    &::placeholder {
        color: rgba(255, 255, 255, 0.35);
    }

    &:focus {
        outline: none !important;
        box-shadow: none !important;
        box-shadow: none !important;
        --tw-ring-shadow: 0 0 #0000 !important;
        --tw-ring-color: transparent !important;
        border-color: rgba(255, 255, 255, 0.06);
    }

    &:disabled {
        opacity: 0.75;
    }
}

.system-prompt-char-count {
    position: absolute;
    right: 14px;
    bottom: 10px;
    font-family: Roboto;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.25);
    pointer-events: none;

    &.is-over {
        color: #ef4444;
    }
}

.system-prompt-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 20px;
    border-top: 1px solid #1F1F21;
}

.system-prompt-btn {
    height: 38px;
    padding: 0 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s, background 0.15s;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.system-prompt-btn-cancel {
    display: flex;
    height: 44px;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    flex: 1 0 0;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.20);
}

.system-prompt-btn-save {
    display: flex;
    height: 44px;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    flex: 1 0 0;
    border-radius: 10px;
    color: #000;
    background: #99F768;
}

@media (max-width: 768px) {
  .system-prompt-textarea {
    font-size: 16px;
  }
}
</style>

